import PropTypes from 'prop-types';
import React from 'react';
import Link from '../../components/Shared/Link';
import Layout from '../../components/Layout/Layout';
import SEO from '../../components/SEO/SEO';
import CTASimple from '../../components/CTASimple/CTASimple';
import theme from '../../styles/theme';
import HeroOverlap from '../../components/HeroOverlap/HeroOverlap';
import ButtonLink from '../../components/Shared/ButtonLink';
import Featurette from '../../components/Featurette/Featurette';
import DirectLendingIcon from '../../components/Shared/Icons/DirectLending';
import CommercialLendingIcon from '../../components/Shared/Icons/CommercialLending';
import FicoIcon from '../../components/Shared/Icons/Fico';
import EscrowIcon from '../../components/Shared/Icons/Escrow';
import Documents from '../../components/Documents/Documents';
import Loans from '../../components/Loans/Loans';
import Error from '../../components/Error/Error';
import Calculators from '../../components/Calculators/Calculators';
import Picture from '../../components/Shared/Picture';
import Styles from './MortgagesAndLoans.styled';
import { getPageContent } from '../../services/dpc';
import useGTM from '../../hooks/useGTM';
import createGTMClass from '../../util/createGTMClass';
import TazwellImage from '../../../public/images/Tazewell_Lifestyle.png';
import CTACardv2 from '../../components/CTACardv2/CTACardv2';

function MortgagesAndLoans({
  data: {
    contentCTAs,
    contentVideos,
    faqPod,
    heroCta,
    heroMedia,
    mobileHeroMedia,
    metaDescription,
    preFooterCta,
    tipsAndToolsPod,
    titleTag,
    customerReviewPod,
    pageTitle,
  } = {},
  error,
  isLoading,
}) {
  if (error) {
    console.error(error);
    return (
      <Layout>
        <Error />
      </Layout>
    );
  }
  const [triggerGTMEvent] = useGTM();

  return (
    <Layout isLoading={isLoading}>
      <SEO title={titleTag} description={metaDescription} />
      <Styles>
        <HeroOverlap
          position='left'
          background={heroMedia?.[0]?.media?.fields?.file?.url}
          mobileBackground={mobileHeroMedia?.[0]?.media?.fields?.file?.url}
          alt={heroMedia?.[0]?.media?.fields.description}
        >
          <h1>{heroCta?.ctaHeader}</h1>
          <p>{heroCta?.ctaIntro}</p>
          <section className='links'>
            <ButtonLink
              appearance='dark'
              to={heroCta?.firstCTAButtonUrl}
              isExternal={true}
              newTab={true}
              size='large'
              className={createGTMClass('apply', 'hero', 'button')}
              onClick={triggerGTMEvent({
                name: 'vmf.cta_click',
                location: 'Mortgage & Loan',
                category: 'loan_application',
                action: 'button',
                label: 'apply',
              })}
            >
              {heroCta?.firstCTAButtonText}
            </ButtonLink>
          </section>
        </HeroOverlap>

        <Loans
          mobileTopPadding='2rem'
          tabletTopPadding='2rem'
          desktopTopPadding='2rem'
          link={{
            to: '/resources/payment-options/',
            title: 'Learn about our payment options',
            onClick: triggerGTMEvent({
              name: 'vmf.cta_click',
              location: 'Mortgage & Loan',
              category: 'loan_evaluation',
              action: 'text',
              label: 'payment_options',
            }),
          }}
        />

        <Calculators />
        <section className='paragraph-section'>
          <Featurette
            primary={
              <div>
                <DirectLendingIcon />
                <h2>Pre-owned Financing by Vanderbilt</h2>
                <p>
                  Already found your perfect manufactured home with a for sale
                  sign in the yard? Our owner-to-owner loan program can help!
                </p>
                <Link
                  to='/applynow/welcome?ReferringTM=null'
                  isExternal={true}
                  newTab={true}
                  className={createGTMClass('apply', 'paragraph', 'text')}
                >
                  Apply now
                </Link>
              </div>
            }
            secondary={
              <Picture
                src='https://images.ctfassets.net/y64yks2od8ab/14KpbqIYjiQ2TW839595oq/cee57c7e701fe1e019b8803bb3c70904/direct-lending.jpg?w=520'
                alt='Direct lending image'
              />
            }
            reverseOnMobile={true}
            center={true}
          />
          <Featurette
            primary={
              <Picture src={TazwellImage} alt='Energy efficient image' />
            }
            secondary={
              <div>
                <CommercialLendingIcon />
                <h2>Community Lending</h2>
                <p>
                  We offer financing for Land-lease communities in select
                  states. Learn about our flexible terms and cash out program
                  for commercial community financing-no refinance required.
                </p>
                <Link to='/community-lending/'>Learn more</Link>
              </div>
            }
            center={true}
          />
        </section>
        <Documents>
          <h2>{contentCTAs?.[0]?.ctaHeader}</h2>
          <p>{contentCTAs?.[0]?.ctaIntro}</p>
          <section className='links'>
            <ButtonLink
              to={
                contentCTAs?.[0]?.firstCTAButtonUrl ||
                '/resources/documents-and-forms'
              }
              appearance='dark'
              className={createGTMClass('view_all_docs', 'cta', 'button')}
              onClick={triggerGTMEvent({
                name: 'vmf.cta_click',
                location: 'Mortgage & Loan',
                category: 'customer_support',
                action: 'button',
                label: 'all_docs',
              })}
            >
              {contentCTAs?.[0].firstCTAButtonText || 'View all documents'}
            </ButtonLink>
          </section>
        </Documents>
        <Featurette
          primary={
            <div>
              <FicoIcon />
              <h2>
                FICO<sup>®</sup> Credit Score
              </h2>
              <p>
                Understanding your FICO<sup>®</sup> Score can help you better
                understand your credit risk and allow you to more responsibly
                manage your financial health.
              </p>
              <Link to='/fico-score/'>Learn more</Link>
            </div>
          }
          secondary={
            <div>
              <EscrowIcon />
              <h2>Escrow</h2>
              <p>
                Escrow is a separate amount collected along with your scheduled
                mortgage payment. The funds are collected to pay your future
                annual homeowners insurance premium and/or taxes.
              </p>
              <Link to='/escrow/'>Learn more</Link>
            </div>
          }
          padding={true}
        />
        <CTACardv2
          preHeaderContent={'Have Questions?'}
          headerContent={`We're Here to Help`}
          buttonText={'Contact Us'}
          buttonUrl={'/contact-us/'}
          gtmClassName={'contact'}
          backgroundBottom={theme.colors.white}
          ctaIntro={
            <>
              <p>
                Existing customers{' '}
                <a href='tel:1-800-970-7250'>1-800-970-7250</a>
              </p>
              <p>
                New customers <a href='tel:1-866-701-0467'>1-866-701-0467</a>
              </p>
            </>
          }
        />
        <CTASimple
          title={preFooterCta?.ctaHeader || 'Dreaming of Homeownership?'}
          link={{
            to: preFooterCta?.firstCTAButtonUrl || '/applynow/welcome/',
            title: preFooterCta?.firstCTAButtonText || 'Apply Now',
            size: 'large',
            isExternal: true,
            newTab: true,
            onClick: triggerGTMEvent({
              name: 'vmf.cta_click',
              location: 'Mortgage & Loan',
              category: 'loan_application',
              action: 'button',
              label: 'apply',
            }),
            className: createGTMClass('apply', 'prefoot_cta', 'button'),
          }}
          background={theme.colors.claytonBlue}
        ></CTASimple>
      </Styles>
    </Layout>
  );
}

MortgagesAndLoans.getInitialProps = async ({}) => {
  try {
    const data = await getPageContent('/manufactured-home-loans/');

    if (data.status !== 200) {
      throw data.statusText;
    }

    return {
      data: data.data.result,
    };
  } catch (error) {
    return {
      error,
    };
  }
};

MortgagesAndLoans.propTypes = {
  match: PropTypes.object,
  history: PropTypes.object,
  location: PropTypes.object,
  data: PropTypes.shape({}),
  error: PropTypes.object,
};

export default MortgagesAndLoans;
